/**
 * The pool standings board is the pool screen's Results tab,
 * `?tab=results` on the existing `/pools` route, with an optional
 * `contestId` layered on top so a specific contest's board is shareable.
 * Every screen and every navigation reads and writes this URL state through
 * the four functions below, never through `search.tab` directly.
 *
 * It used to be `?view=standings`, and `aliasLegacyPoolView` keeps every link
 * that still carries that address resolving here (ISSUE-952).
 */
import {
  openPoolTabSearch,
  readPoolTab,
  type typePoolTab,
} from "../poolScreen/poolScreenRoute";

export const POOL_STANDINGS_TAB: typePoolTab = "results";

export function isPoolStandingsOpen(search: {
  poolId?: string | undefined;
  tab?: string | undefined;
}): boolean {
  return (
    readPoolTab(search) === POOL_STANDINGS_TAB &&
    typeof search.poolId === "string" &&
    search.poolId !== ""
  );
}

export function openPoolStandingsSearch<
  TSearch extends Record<string, unknown>,
>(
  search: TSearch,
  contestId?: string,
): TSearch & { tab: typePoolTab; contestId?: string } {
  return {
    ...openPoolTabSearch(search, POOL_STANDINGS_TAB),
    ...(contestId === undefined || contestId === "" ? {} : { contestId }),
  };
}

export function closePoolStandingsSearch<
  TSearch extends Record<string, unknown>,
>(search: TSearch): TSearch {
  const dropped = new Set<string>(["view", "tab", "contestId"]);
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !dropped.has(key)),
  ) as TSearch;
}

/**
 * The Results tab's two views live in the tab's own sub-address, `?panel=`
 * (ISSUE-1317). Absent means Standings, so every link that already points at
 * the Results tab still lands on the board.
 */
export const RESULTS_PICKS_PANEL = "picks";
export type typeResultsView = "standings" | "picks";

export function readResultsView(
  panel: string | null | undefined,
): typeResultsView {
  return panel === RESULTS_PICKS_PANEL ? "picks" : "standings";
}

export function openResultsViewSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
  view: typeResultsView,
): TSearch {
  const kept = Object.fromEntries(
    Object.entries(search).filter(([key]) => key !== "panel"),
  ) as TSearch;
  return view === "picks" ? { ...kept, panel: RESULTS_PICKS_PANEL } : kept;
}

/**
 * Switching contest (ISSUE-1331). The round and entry picked under the old
 * contest mean nothing under the new one, so they go with it.
 */
export function openResultsContestSearch<
  TSearch extends Record<string, unknown>,
>(search: TSearch, contestId: string): TSearch & { contestId: string } {
  const dropped = new Set<string>(["roundId", "entryId"]);
  const kept = Object.fromEntries(
    Object.entries(search).filter(([key]) => !dropped.has(key)),
  ) as TSearch;
  return { ...kept, contestId };
}

/**
 * One member's rounds open as `?sheet=standings-member&memberId=<userId>`
 * (ISSUE-1318), so browser back closes the sheet and a refresh reopens it.
 */
export const MEMBER_ROUNDS_SHEET_NAME = "standings-member";
